import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  StatusCandidatePositionOptions,
  candidatesApi,
  useAddCandidateToPositionAgainMutation,
  useAddCandidateToPositionMutation,
  useRemoveCandidateFromPositionMutation,
  useUpdateCandidatePositionHighlightMutation,
  useUpdateCandidatePositionStatusMutation,
} from '$store/modules/candidates';
import ActiveValues from '$constants/ActiveValues';
import { companiesApi } from '$store/modules/companies';
import { CandidateData } from '$types/candidates';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import { SIMERA_FEE } from '$constants/SimeraOperations';
import capitalizedName from '$utils/capitalizedName';
import { countriesCodes } from '$constants/countriesCodes';
import computeYearsOfExperience from '$utils/computeYearsOfExperience';
import SPKCandidateHiddenSkills from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateHiddenSkills';
import SPKCandidateCardSkill from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateSkills';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import { ReactComponent as Star } from '$icons/uncolored/star.svg';
import { ReactComponent as MakeOffer } from '$icons/uncolored/makeOffer.svg';
import { ReactComponent as ThumbDown } from '$icons/uncolored/thumbs-down.svg';
import { ReactComponent as Interview } from '$icons/uncolored/comments.svg';
import { ReactComponent as Verified } from '$icons/uncolored/verified.svg';
import { ReactComponent as View } from '$icons/uncolored/eye.svg';
import { ReactComponent as Share } from '$icons/uncolored/share-link.svg';
import { ReactComponent as Add } from '$icons/uncolored/Add.svg';
import { ReactComponent as Copy } from '$icons/copy.svg';
import { ReactComponent as HireCandidate } from '$icons/uncolored/PersonAdd.svg';
import { toast } from 'react-toastify';
import MakeOfferConfirmModal from '$components/Positions/CandidateSummaryCardList/CandidateSummaryCard/MakeOfferConfirmModal/MakeOfferConfirmModal';
import { singlePositionSlice } from '$store/modules/position/position.reducer';
import copyToClipboard from '$utils/copyToClipboard';
import { successHandler } from '$store/utils';
import getUserType from '$utils/getUserType';
import { UserTypes } from '$types/UserTypes';
import SPKToast from '$components/SPKToast/SPKToast';
import { Tooltip } from 'primereact/tooltip';
import dayjs from 'dayjs';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import HireConfirmModalContent from '$components/Positions/CandidateSummaryCardList/CandidateSummaryCard/HireConfirmModal/HireConfirmModalContent/HireConfirmModalContent';
import { tracker } from '$utils/tracker';
import { useAppSelector } from '$hooks/use-app-selector.hook';

import SPKEmployerCandidateShowcase from './SPKEmployerCandidateShowcase/SPKEmployerCandidateShowcase';

interface Props {
  candidate: CandidateData;
  position: PositionInternalDto;
  activeValue: ActiveValues;
  feeAmount?: number;
  showSalaryRange?: boolean;
}

const SPKEmployerPositionCandidateCard: FC<Props> = ({
  candidate,
  position,
  activeValue,
  feeAmount,
  showSalaryRange,
}) => {
  const resolvedFee = feeAmount ?? SIMERA_FEE;
  const { currency, currencyCode, usdToCurrencyConverter } = useAppSelector(
    (state) => state.brand
  );
  const isHighlighted = useMemo(() => {
    if (activeValue === ActiveValues.find) return false;
    return (
      position.CandidatesPositionStatus?.find(
        (singlePos) => singlePos.Candidate?.Id === candidate.Id
      )?.Highlight || false
    );
  }, [activeValue, candidate.Id, position.CandidatesPositionStatus]);
  const userType = getUserType();
  const { positionId: positionIdFromParam } = useParams();
  const positionId = (
    positionIdFromParam ? Number(positionIdFromParam) : position.Id
  ) as number;
  const [showMakeOfferModal, setMakeOfferModal] = useState<boolean>(false);
  const [overflowStart, setOverflowStart] = useState<number>();
  const [hiddenSkills, setHiddenSkills] = useState<
    CandidateData['CandidateSkills']
  >([]);
  const [visibleSkills, setVisibleSkills] = useState<
    CandidateData['CandidateSkills']
  >([]);
  const [showHireModal, setShowHireModal] = useState(false);

  const dispatch = useDispatch();

  const [updateCandidatePositionStatus] =
    useUpdateCandidatePositionStatusMutation();
  const [addCandidateToPositionAgain] =
    useAddCandidateToPositionAgainMutation();
  const [addCandidateToPosition] = useAddCandidateToPositionMutation();
  const [updateCandidatePositionHighlight] =
    useUpdateCandidatePositionHighlightMutation();
  const [removeCandidateFromPosition] =
    useRemoveCandidateFromPositionMutation();

  const [t, i18n] = useTranslation('global');

  const yearsOfExperience = useMemo(() => {
    return computeYearsOfExperience(
      candidate.CandidateWorkExperience as CandidateData['CandidateWorkExperience']
    );
  }, [candidate.CandidateWorkExperience]);

  const countryEmoji = useMemo(() => {
    return countriesCodes.find(
      (singleCountry) =>
        singleCountry.name.toLowerCase() ===
        candidate?.ResidenceCountry?.toLowerCase()
    )?.code;
  }, [candidate?.ResidenceCountry]);

  useEffect(() => {
    setMakeOfferModal(false);
  }, [candidate]);

  const handleMakeOfferModal = (show: boolean) => setMakeOfferModal(show);

  // const interviewAgainHandler = () => {
  //   setShowModalConfirmationInterview(true);
  // };

  const skillsContainerRef = useRef<HTMLDivElement | null>(null);

  const changeCandidatePositionStatusHandler = (
    newStatus: StatusCandidatePositionOptions
  ) => {
    if ('CandidatesPositionStatus' in candidate) {
      if (!candidate.CandidatesPositionStatus) return;
      updateCandidatePositionStatus({
        data: {
          CandidatePositionStatusId: candidate.CandidatesPositionStatus[0].Id,
          Status: newStatus,
        },
        lang: i18n.language as 'en' | 'es',
      }).then(() => {
        dispatch(candidatesApi.util.invalidateTags(['Candidates']));
        dispatch(companiesApi.util.invalidateTags(['PositionsList']));
        if (newStatus === 'new' && activeValue === ActiveValues.ShortList) {
          tracker.emit(`Candidate Removed from Shortlist`, {
            candidate_id: candidate.Id,
            position_id: positionId,
          });
        } else if (newStatus === ActiveValues.ShortList) {
          tracker.emit(`Candidate Added to Shortlist`, {
            candidate_id: candidate.Id,
            position_id: positionId,
          });
        }
      });
    }
  };

  const addToPositionHandler = async () => {
    const addCandidate = await addCandidateToPosition({
      data: {
        candidateId: candidate.Id,
        positionId: position.Id,
      },
    });

    if ('data' in addCandidate) {
      dispatch(companiesApi.util.invalidateTags(['PositionsList']));
      dispatch(
        companiesApi.util.invalidateTags([{ type: 'Company', id: position.Id }])
      );

      SPKToast(t('PositionCard.Success.CandidateAdded'));
    } else {
      SPKToast(t('PositionCard.Error.SomethingWentWrong'), { type: 'Error' });
    }
  };

  const addAgainToPositionHandler = async () => {
    const addAgainCandidate = await addCandidateToPositionAgain({
      data: {
        candidateId: candidate.Id,
        positionId: position.Id,
      },
    });
    if ('data' in addAgainCandidate) {
      dispatch(companiesApi.util.invalidateTags(['PositionsList']));
      SPKToast(t('PositionCard.Success.CandidateAddedAgain'));
    } else {
      SPKToast(t('PositionCard.Error.SomethingWentWrong'), { type: 'Error' });
    }
  };

  const toggleCandidateHighlight = useCallback(async () => {
    const highlightData = await updateCandidatePositionHighlight({
      candidatePositionStatusId: candidate.CandidatesPositionStatus?.[0]
        .Id as number,
      highlightCandidate: !isHighlighted,
    });

    if ('data' in highlightData) {
      await dispatch(companiesApi.util.invalidateTags(['PositionsList']));
    }
  }, [
    candidate.CandidatesPositionStatus,
    dispatch,
    isHighlighted,
    updateCandidatePositionHighlight,
  ]);

  const onOverflowHandler = (index: number) => {
    setOverflowStart(index);
  };

  const removeFromPositionHandler = async () => {
    removeCandidateFromPosition({
      data: {
        candidateId: candidate.Id,
        positionId,
      },
    }).then((resp) => {
      if ('data' in resp) {
        dispatch(companiesApi.util.invalidateTags(['PositionsList']));
        dispatch(
          companiesApi.util.invalidateTags([
            { type: 'Company', id: position.Id },
          ])
        );
        SPKToast(t('PositionCard.Success.CandidateRemoved'));
      } else {
        SPKToast(t('PositionCard.Error.SomethingWentWrong'), { type: 'Error' });
      }
    });
  };

  const candidateScore = useMemo(() => {
    return position?.CandidatesPositionStatus?.find(
      (singleCandidate) => singleCandidate.Candidate?.Id === candidate.Id
    )?.Candidate?.Score;
  }, [candidate.Id, position.CandidatesPositionStatus]);

  const demandScoreColor = useMemo(() => {
    const scoreColor = {
      low: 'bg-[#FCDAC4] text-[#A44101]',
      medium: 'bg-[#CFD0FF] text-[#2C30D7]',
      high: 'bg-[#BFEFEC] text-[#016F68]',
    };

    if (candidateScore > 90) return scoreColor.high;
    if (candidateScore > 80) return scoreColor.medium;
    return scoreColor.low;
  }, [candidateScore]);

  const makeOfferHandler = useCallback(() => {
    if (!position.CandidatesPositionStatus) return;
    const existingOffer = position.CandidatesPositionStatus.find(
      (singleCandidatePositionStatus) =>
        (singleCandidatePositionStatus.Status as unknown as ActiveValues) ===
        ActiveValues.OfferSent
    );

    if (existingOffer) {
      toast(t(t('CandidatePositionSummaryCard.ExistingOffer')), {
        type: 'error',
        hideProgressBar: false,
      });
    } else {
      setMakeOfferModal(true);
    }
  }, [position.CandidatesPositionStatus, t]);

  const copySPKURLToClipboardHandler = async () => {
    const spkUrl = import.meta.env.REACT_APP_SPK_URL;
    await copyToClipboard(`${spkUrl}/spk/${candidate.Id}`).then(() => {
      successHandler('Candidate Profile URL copied to clipboard', true);
    });
  };

  const copyCandidateIdHandler = async (candidateId: number) => {
    await copyToClipboard(`${candidateId}`).then(() => {
      SPKToast('Candidate Id copied into clipboard');
    });
  };

  useEffect(() => {
    if (!candidate.CandidateSkills) return;

    if (!overflowStart && candidate.CandidateSkills) {
      setVisibleSkills(candidate.CandidateSkills);
      return;
    }

    const visible = candidate.CandidateSkills.slice(0, overflowStart);
    const hidden = candidate.CandidateSkills.slice(overflowStart);

    setVisibleSkills(visible);
    setHiddenSkills(hidden);
  }, [candidate.CandidateSkills, overflowStart]);

  return (
    <li
      className={`flex flex-col md:flex-row rounded-xl ${
        isHighlighted &&
        userType === UserTypes.EMPLOYER &&
        'border-[3px] border-employer-primary'
      } ${
        isHighlighted &&
        userType === UserTypes.INTERNAL &&
        'border-[3px] border-internal-main'
      }`}
      data-testid="SPKEmployerPositionCandidatesCard"
      data-candidate-id={candidate.Id}
    >
      {/* Left section */}
      <div className="w-full md:w-6/12 p-4 bg-white rounded-t-xl md:rounded-tr-none md:rounded-l-xl">
        {/* Name, last job and country */}
        <div className="flex flex-row gap-4 mb-8 items-center">
          <div className="w-10 min-w-10">
            <DefaultIcon2
              firstName={candidate.User?.FirstName}
              lastName={candidate.User?.LastName}
              fontSize="text-xl"
              hideBorder
              picture={candidate?.User?.ProfilePicture?.Url}
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:divide-x divide-gray-400 *:px-4 -mx-4 sm:items-center min-w-0">
            <div className="gap-1 flex flex-col">
              <div
                {...(userType === UserTypes.INTERNAL && {
                  type: 'button',
                  role: 'button',
                  onClick: () => {
                    window.open(
                      `/simera-dashboard/positions/${positionId}/candidates/${candidate.Id}`,
                      '_blank'
                    );
                  },
                })}
                className={`flex max-w-xs min-w-36 ${
                  userType === UserTypes.INTERNAL &&
                  'hover:cursor-pointer hover:underline hover:text-internal-link'
                }`}
              >
                <span
                  className="font-bold text-base truncate max-w-48"
                  title={`${capitalizedName(
                    candidate.User?.FirstName
                  )} ${capitalizedName(candidate.User?.LastName)}`}
                >
                  {`${capitalizedName(
                    candidate.User?.FirstName
                  )} ${capitalizedName(candidate.User?.LastName)}`}
                </span>
              </div>
              {userType === UserTypes.INTERNAL && (
                <div className="flex flex-row gap-4 text-xs">
                  <button
                    className="flex flex-row gap-2"
                    type="button"
                    onClick={() => copyCandidateIdHandler(candidate.Id)}
                  >
                    ID: {candidate.Id}
                    <Copy className="h-4 w-4" />
                  </button>
                  {candidate.CandidatesPositionStatus?.[0]?.CreateTime && (
                    <>
                      <Tooltip
                        target={`.candidatePositionStatusCreateTime-${candidate.Id}`}
                      />
                      <div
                        className={`candidatePositionStatusCreateTime-${candidate.Id}`}
                        aria-hidden
                        data-testid="cps-create-date"
                        data-pr-tooltip={t(
                          'CandidatePositionSummaryCard.TooltipText'
                        )}
                        data-pr-position="bottom"
                      >
                        {dayjs(
                          candidate.CandidatesPositionStatus?.[0]?.CreateTime
                        ).format('MM/DD/YYYY')}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="font-bold flex flex-row items-center min-w-20">
              {candidate?.CandidateWorkExperience?.[0]?.JobTitle && (
                <span
                  className="truncate max-w-40"
                  title={candidate?.CandidateWorkExperience?.[0]?.JobTitle}
                >
                  {candidate?.CandidateWorkExperience?.[0]?.JobTitle}
                </span>
              )}
            </div>
          </div>
          <div className="inline-flex flex-wrap gap-y-2">
            {candidate.ResidenceCountry && (
              <span className="text-nowrap font-light text-xs border border-neutral-200 text-neutral-600 rounded-md px-2 py-1 flex gap-2 items-center self-center mr-2">
                <span className={`fi fi-${countryEmoji?.toLowerCase()}`} />
                {candidate.ResidenceCountry}
              </span>
            )}
            <span
              className={`md:text-nowrap text-xs px-2 py-1 items-center flex font-bold rounded self-center mr-2 ${demandScoreColor}`}
            >
              {t('PositionCard.Demand')} {candidateScore}
            </span>
            {isHighlighted && (
              <span className="md:text-nowrap text-xs px-2 py-1 items-center flex gap-1 font-bold rounded self-center text-white bg-[#2C30D7]">
                <i className="pi pi-star" />
                {t('PositionCard.Highlight')}
              </span>
            )}
          </div>
        </div>

        {/* General Info */}
        <div className="text-sm mb-2">
          <div className="flex flex-row divide-x divide-gray-400 *:px-2 -mx-2">
            {candidate?.CandidateWorkExperience?.[0]?.Company && (
              <span className="flex flex-wrap text-black/50">
                Highlight:{' '}
                <span className="text-black ml-1">
                  {candidate?.CandidateWorkExperience?.[0]?.Company}
                </span>
              </span>
            )}
            <span>
              <span className="text-black/50">Exp:</span> {yearsOfExperience}+
              yrs
            </span>
            <span>
              <span className="text-black/50">
                {t('PositionCard.MontlySalaryWithCurrency', { currencyCode })}:
              </span>{' '}
              {showSalaryRange
                ? `${currency}${usdToCurrencyConverter(
                    (candidate?.CandidateJobInterest?.SalaryLowerBound || 0) +
                      resolvedFee
                  )} - ${currency}${usdToCurrencyConverter(
                    (candidate?.CandidateJobInterest?.SalaryUpperBound || 0) +
                      resolvedFee
                  )}/m ${currencyCode}`
                : `${currency}${usdToCurrencyConverter(
                    (candidate?.CandidateJobInterest?.SalaryLowerBound || 0) +
                      resolvedFee
                  )}/m ${currencyCode}`}
            </span>
          </div>
        </div>

        {/* Skills */}
        <div className="flex flex-row items-center gap-2 mb-8">
          <div
            className="flex overflow-x-hidden gap-2 text-xs font-semibold text-gray-500 max-w-full"
            ref={skillsContainerRef}
          >
            {candidate.IsSimeraAICertified && (
              <span className="text-xs rounded border border-gray-300 p-1 text-nowrap flex flex-row gap-2">
                <span>Simera AI Certified</span>
                <Verified
                  className={`w-4 h-4 ${
                    userType === UserTypes.INTERNAL
                      ? 'fill-internal-main'
                      : 'fill-employer-primary'
                  }`}
                />
              </span>
            )}
            {visibleSkills &&
              visibleSkills
                .slice(0, overflowStart)
                .map((singleSkill, index) => {
                  return (
                    <SPKCandidateCardSkill
                      key={`visibleSkill_${singleSkill.Skill.Title}_${singleSkill.Id}_${positionId}`}
                      skill={singleSkill.Skill.Title}
                      index={index}
                      containerRef={
                        skillsContainerRef.current as HTMLDivElement
                      }
                      setOverflowIndex={onOverflowHandler}
                      isVerified={singleSkill.IsVerified}
                      shouldHighlight={
                        !!position.Skills_position.find((item) => {
                          return (
                            item?.Title?.toLowerCase()?.trim() ===
                            singleSkill?.Skill?.Title?.toLowerCase()?.trim()
                          );
                        })
                      }
                    />
                  );
                })}
          </div>
          {hiddenSkills && hiddenSkills.length > 0 && (
            <SPKCandidateHiddenSkills
              hiddenSkills={hiddenSkills.map(({ Id, Skill, IsVerified }) => ({
                Id,
                Skill: { title: Skill.Title },
                IsVerified,
                shouldHighlight: !!position.Skills_position.find((item) => {
                  return (
                    item?.Title?.toLowerCase()?.trim() ===
                    Skill?.Title?.toLowerCase()?.trim()
                  );
                }),
              }))}
              uniqueId={positionId}
            />
          )}
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            className="flex flex-row items-center gap-2 text-sm text-spk-highContrast"
            onClick={() => {
              dispatch(
                singlePositionSlice.actions.setSelectedCandidate(candidate)
              );
              dispatch(singlePositionSlice.actions.setOpenFullSPKProfile(true));
              dispatch(singlePositionSlice.actions.setIsLoadingIframe(true));
            }}
          >
            <View
              className={`${
                userType === UserTypes.INTERNAL
                  ? 'fill-internal-main'
                  : 'fill-employer-primary'
              } w-4 h-4`}
            />
            {t('PositionCard.FullProfile')}
          </button>
          <button
            type="button"
            className="flex flex-row items-center gap-2 text-sm text-spk-highContrast"
            onClick={copySPKURLToClipboardHandler}
          >
            <Share
              className={`${
                userType === UserTypes.INTERNAL
                  ? 'fill-internal-main'
                  : 'fill-employer-primary'
              } w-4 h-4`}
            />
            {t('PositionCard.ShareProfile')}{' '}
          </button>
        </div>
      </div>
      <div className=" bg-white items-center hidden md:flex flex-row py-4">
        <div className="border-l border-employer-secondary h-full" />
      </div>
      <div className="w-full md:w-6/12 flex flex-row">
        {/* Middle section */}
        <div className="w-8/12 py-4 px-12 bg-white rounded-bl-xl md:rounded-bl-none">
          <p className="font-bold mb-2">Showcase</p>
          <SPKEmployerCandidateShowcase
            candidate={candidate}
            positionId={position.Id}
          />
        </div>

        {/* Right section */}
        <div className="w-4/12 bg-spk-neutral-10 rounded-br-xl md:rounded-r-xl">
          <div className="font-bold gap-6 flex flex-col py-4 md:py-8 px-4 text-xs">
            {activeValue !== ActiveValues.hired && (
              <>
                {/* When its on find tab */}
                {activeValue === ActiveValues.find && (
                  <button
                    type="button"
                    className="text-internal-main flex flex-row gap-2 items-center"
                    onClick={addToPositionHandler}
                  >
                    <span className="bg-internal-main/15 px-4 py-2 rounded-full flex flex-row gap-2 items-center">
                      <Add className="fill-internal-main w-3 h-3" />
                      {t('PositionCard.AddToPosition')}
                    </span>
                  </button>
                )}
                {/* When its on declined tab */}
                {activeValue === ActiveValues.declined &&
                  userType === UserTypes.INTERNAL && (
                    <button
                      type="button"
                      className="text-internal-main flex flex-row gap-2 items-center"
                      onClick={addAgainToPositionHandler}
                    >
                      <span className="bg-internal-main/15 px-4 py-2 rounded-full flex flex-row gap-2 items-center">
                        <span className="pi pi-refresh" />
                        {t('PositionCard.AddAgain')}
                      </span>
                    </button>
                  )}
                {/* When its not on find, declined tab or hired  */}
                {activeValue !== ActiveValues.find &&
                  activeValue !== ActiveValues.declined && (
                    <>
                      {/* Request interview */}
                      {activeValue !== ActiveValues.requestInterview &&
                        ((userType === UserTypes.INTERNAL &&
                          activeValue !== ActiveValues.ShortList) ||
                          (userType === UserTypes.EMPLOYER &&
                            (activeValue === ActiveValues.ShortList ||
                              activeValue === ActiveValues.New))) && (
                          <button
                            type="button"
                            className={`${
                              userType === UserTypes.INTERNAL
                                ? 'text-internal-main'
                                : 'text-employer-primary '
                            } flex flex-row gap-2 items-center`}
                            onClick={() => {
                              dispatch(
                                singlePositionSlice.actions.setSelectedCandidate(
                                  candidate
                                )
                              );
                              dispatch(
                                singlePositionSlice.actions.setOpenConfirmationInterviewModal(
                                  true
                                )
                              );
                            }}
                          >
                            <span
                              className={`${
                                userType === UserTypes.INTERNAL
                                  ? 'bg-internal-main/15'
                                  : 'bg-employer-primary/15'
                              } px-4 py-2 rounded-full flex flex-row gap-2`}
                            >
                              <Interview
                                className={`${
                                  userType === UserTypes.INTERNAL
                                    ? 'fill-internal-main'
                                    : 'fill-employer-primary'
                                } w-4 h-4`}
                              />
                              {t('EmployerPositions.RequestInterview')}
                            </span>
                          </button>
                        )}
                      {/* Highlight */}
                      {userType === UserTypes.INTERNAL &&
                        activeValue !== ActiveValues.interested && (
                          <button
                            type="button"
                            onClick={toggleCandidateHighlight}
                            className="flex flex-row gap-2 items-center px-4 text-internal-main"
                          >
                            <Verified className="fill-internal-main w-4 h-4" />
                            {isHighlighted
                              ? t(
                                  'CandidatePositionSummaryCard.UnhighlightCandidate'
                                )
                              : t(
                                  'CandidatePositionSummaryCard.HighlightCandidate'
                                )}
                          </button>
                        )}
                      {/* Shortlist btn on other tabs than shortlist */}
                      {candidate.CandidatesPositionStatus &&
                        candidate.CandidatesPositionStatus.length > 0 &&
                        (candidate.CandidatesPositionStatus[0].Status ===
                          ActiveValues.New ||
                          (candidate.CandidatesPositionStatus[0].Status ===
                            ActiveValues.interested &&
                            userType === UserTypes.EMPLOYER)) && (
                          <button
                            type="button"
                            className={`${
                              userType === UserTypes.INTERNAL
                                ? 'text-internal-main'
                                : 'text-employer-primary'
                            } flex flex-row gap-2 items-center px-4`}
                            onClick={() =>
                              changeCandidatePositionStatusHandler(
                                ActiveValues.ShortList
                              )
                            }
                          >
                            <Star
                              className={`${
                                userType === UserTypes.INTERNAL
                                  ? 'fill-internal-main'
                                  : 'fill-employer-primary'
                              } w-4 h-4`}
                            />
                            {t('EmployerPositions.ShortList')}
                          </button>
                        )}
                      {/* Shortlist on shortlist tab */}
                      {candidate.CandidatesPositionStatus &&
                        candidate.CandidatesPositionStatus.length > 0 &&
                        candidate.CandidatesPositionStatus[0].Status ===
                          ActiveValues.ShortList && (
                          <button
                            type="button"
                            className={`${
                              userType === UserTypes.INTERNAL
                                ? 'text-internal-main'
                                : 'text-employer-primary'
                            } flex flex-row gap-2 items-center px-4`}
                            onClick={() =>
                              changeCandidatePositionStatusHandler(
                                ActiveValues.New
                              )
                            }
                          >
                            <Star
                              className={`${
                                userType === UserTypes.INTERNAL
                                  ? 'fill-internal-main'
                                  : 'fill-employer-primary'
                              } w-4 h-4`}
                            />
                            {t('EmployerPositions.RemoveShortList')}
                          </button>
                        )}
                      {userType === UserTypes.EMPLOYER && (
                        <button
                          type="button"
                          className="text-employer-primary flex flex-row gap-2 items-center px-4"
                          onClick={makeOfferHandler}
                        >
                          <MakeOffer className="fill-employer-primary w-4 h-4" />
                          {t('EmployerPositions.MakeOffer')}
                        </button>
                      )}
                      {userType === UserTypes.INTERNAL &&
                        activeValue !== ActiveValues.interested && (
                          <button
                            type="button"
                            className="text-internal-main flex flex-row gap-2 items-center px-4"
                            onClick={() => setShowHireModal(true)}
                          >
                            <HireCandidate className="fill-internal-main w-4 h-4" />
                            {t('CandidatePositionSummaryCard.HireCandidate')}
                          </button>
                        )}
                      {!(
                        userType === UserTypes.INTERNAL &&
                        activeValue === ActiveValues.interested
                      ) && (
                        <button
                          type="button"
                          className="text-spk-brightRed flex flex-row gap-2 items-center px-4"
                          onClick={() => {
                            dispatch(
                              singlePositionSlice.actions.setSelectedCandidate(
                                candidate
                              )
                            );
                            dispatch(
                              singlePositionSlice.actions.setOpenNotAFitModal(
                                true
                              )
                            );
                          }}
                        >
                          <ThumbDown className="fill-spk-brightRed w-4 h-4" />
                          {t('EmployerPositions.NotAFit')}
                        </button>
                      )}
                      {userType === UserTypes.INTERNAL &&
                        activeValue !== ActiveValues.interested && (
                          <button
                            type="button"
                            onClick={removeFromPositionHandler}
                            className="flex flex-row gap-2 items-center px-4 text-spk-brightRed"
                          >
                            <i className="pi pi-times-circle" />
                            {t('CandidatePositionSummaryCard.RemoveCandidate')}
                          </button>
                        )}
                    </>
                  )}
              </>
            )}
            {userType === UserTypes.INTERNAL &&
              activeValue === ActiveValues.interested && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      changeCandidatePositionStatusHandler(ActiveValues.New)
                    }
                    className={`flex flex-row gap-2 items-center px-4 ${
                      userType === UserTypes.INTERNAL
                        ? 'text-internal-main'
                        : 'text-spk-brightBlue'
                    }`}
                  >
                    <i className="pi pi-user-plus text-base" />
                    {t('CandidatePositionSummaryCard.Roster')}
                  </button>
                  <button
                    type="button"
                    className="text-spk-brightRed flex flex-row gap-2 items-center px-4"
                    onClick={() => {
                      dispatch(
                        singlePositionSlice.actions.setSelectedCandidate(
                          candidate
                        )
                      );
                      dispatch(
                        singlePositionSlice.actions.setOpenNotAFitModal(true)
                      );
                    }}
                  >
                    <ThumbDown className="fill-spk-brightRed w-4 h-4" />
                    {t('EmployerPositions.NotAFit')}
                  </button>
                </>
              )}
          </div>
        </div>
      </div>
      {/* Modals */}
      {candidate.CandidatesPositionStatus &&
        candidate.CandidatesPositionStatus.length > 0 && (
          <MakeOfferConfirmModal
            modalIsOpen={showMakeOfferModal}
            handleClose={handleMakeOfferModal}
            candidatePositionStatusId={
              candidate?.CandidatesPositionStatus?.[0]?.Id
            }
            candidateId={candidate.Id}
          />
        )}
      {activeValue !== ActiveValues.find &&
        candidate.CandidatesPositionStatus &&
        candidate.CandidatesPositionStatus.length > 0 && (
          <ModalInTailwind
            isOpen={showHireModal}
            closeModal={() => setShowHireModal(false)}
          >
            <div className="bg-white rounded flex gap-4 flex-col p-8">
              <HireConfirmModalContent
                handleClose={() => {
                  setShowHireModal(false);
                }}
                candidatePositionStatusId={
                  candidate.CandidatesPositionStatus![0].Id
                }
                positionId={positionId}
                candidateId={candidate.Id}
              />
            </div>
          </ModalInTailwind>
        )}
    </li>
  );
};

SPKEmployerPositionCandidateCard.defaultProps = {
  feeAmount: undefined,
  showSalaryRange: false,
};

export default SPKEmployerPositionCandidateCard;
